/**
 * Shared real-time connections for open notes. One WebSocket is multiplexed for all plaintext
 * notes; each open note keeps its Y.Doc alive while its tab is open, so switching tabs or brief
 * network drops never discard unsynced typing.
 *
 * Offline copies: every plaintext note opened here is also kept in this browser's IndexedDB
 * (per user, per note and per content epoch), so a reload while the server is unreachable still
 * shows the note and keeps unsynced edits until they reach the server. End-to-end encrypted
 * notes are handled by ../crypto/encryptedCollab and only ever cache ciphertext.
 */
import * as Y from "yjs";
import type { Awareness } from "y-protocols/awareness";
import { HocuspocusProvider, HocuspocusProviderWebsocket } from "@hocuspocus/provider";
import { IndexeddbPersistence } from "y-indexeddb";
import { idbDel, idbGet, idbKeys } from "@/lib/idb";

export type SyncState = "connecting" | "synced" | "saving" | "saved" | "offline" | "save-error" | "forbidden" | "revoked" | "locked";

export interface CollabEntry {
  noteId: string;
  kind: "plain" | "encrypted";
  epoch: number;
  doc: Y.Doc;
  awareness: Awareness | null;
  state: SyncState;
  lastSavedAt: string | null;
  error: string | null;
  everSynced: boolean;
  /** True once text from this browser's offline copy is shown (before or without the server). */
  fromOfflineCopy: boolean;
  /** Archived (the note, or its notebook or project): the server accepts no change. */
  frozen?: boolean;
  listeners: Set<() => void>;
  hasUnsynced(): boolean;
  destroy(): void;
}

let socket: HocuspocusProviderWebsocket | null = null;
const entries = new Map<string, CollabEntry>();

const COLORS = ["#2f6db3", "#a8432f", "#2f8f5b", "#8a4fb0", "#b07a1f", "#1f8a8a"];

export const plainCacheName = (userId: string, noteId: string, epoch: number) => `brainlog-claude-note-${userId}-${noteId}-${epoch}`;

let offlineEnabled = true;
try {
  offlineEnabled = localStorage.getItem("brainlog-claude.offline-copies") !== "off";
} catch {
  /* default on */
}
export const offlineCopiesEnabled = () => offlineEnabled;
export function setOfflineCopiesEnabled(v: boolean) {
  offlineEnabled = v;
  try {
    localStorage.setItem("brainlog-claude.offline-copies", v ? "on" : "off");
  } catch {
    /* ignore */
  }
}

function getSocket() {
  if (!socket) {
    const proto = location.protocol === "https:" ? "wss" : "ws";
    socket = new HocuspocusProviderWebsocket({ url: `${proto}://${location.host}/collab`, maxAttempts: 0, delay: 1000, maxDelay: 10_000 });
  }
  return socket;
}

export function notify(e: CollabEntry) {
  for (const l of e.listeners) l();
}

export function setEntry(e: CollabEntry, patch: Partial<CollabEntry>) {
  Object.assign(e, patch);
  notify(e);
}

/** Delete offline copies of a note (all epochs, or all but `keepEpoch`). */
export async function dropPlainCaches(userId: string, noteIds: string[], keepEpoch?: { noteId: string; epoch: number }, knownEpochs?: Map<string, number>) {
  if (!noteIds.length || typeof indexedDB === "undefined") return;
  if (!indexedDB.databases) {
    // Without database listing, delete the names every epoch up to the current one would use.
    for (const id of noteIds) {
      for (let e = 0; e <= (knownEpochs?.get(id) ?? 0); e++) {
        if (keepEpoch && keepEpoch.noteId === id && keepEpoch.epoch === e) continue;
        indexedDB.deleteDatabase(plainCacheName(userId, id, e));
      }
    }
    return;
  }
  const names = (await indexedDB.databases().catch(() => [])).map((d) => d.name ?? "");
  const keep = keepEpoch ? plainCacheName(userId, keepEpoch.noteId, keepEpoch.epoch) : null;
  for (const id of noteIds) {
    const prefix = `brainlog-claude-note-${userId}-${id}-`;
    for (const n of names) if (n.startsWith(prefix) && n !== keep) indexedDB.deleteDatabase(n);
  }
}

/**
 * Remove plaintext offline copies of notes that are now end-to-end encrypted (called whenever a
 * note list shows them), so text written before encryption does not stay on this device.
 */
export async function forgetPlaintextCopies(userId: string, encryptedNotes: { id: string; contentEpoch?: number }[]) {
  const encryptedNoteIds = encryptedNotes.map((n) => n.id);
  if (!encryptedNoteIds.length) return;
  await dropPlainCaches(userId, encryptedNoteIds, undefined, new Map(encryptedNotes.map((n) => [n.id, n.contentEpoch ?? 0])));
  for (const id of encryptedNoteIds) {
    const cached = await idbGet<{ note?: { encryptionScopeId?: string | null } }>(userId, `note:${id}`);
    if (cached && !cached.note?.encryptionScopeId) await idbDel(userId, `note:${id}`);
  }
}

function createPlainEntry(noteId: string, user: { id: string; displayName: string }, epoch: number): CollabEntry {
  const doc = new Y.Doc();
  let persistence: IndexeddbPersistence | null = null;
  const entry: CollabEntry = {
    noteId,
    kind: "plain",
    epoch,
    doc,
    awareness: null,
    state: "connecting",
    lastSavedAt: null,
    error: null,
    everSynced: false,
    fromOfflineCopy: false,
    listeners: new Set(),
    hasUnsynced: () => provider.hasUnsyncedChanges,
    destroy: () => {
      provider.destroy();
      void persistence?.destroy();
      doc.destroy();
    },
  };
  const forget = () => {
    // No access any more: the offline copy must not outlive it.
    void persistence?.clearData();
    persistence = null;
  };
  if (offlineEnabled) {
    persistence = new IndexeddbPersistence(plainCacheName(user.id, noteId, epoch), doc);
    persistence.once("synced", () => {
      if (doc.getText("content").length && !entry.everSynced) setEntry(entry, { fromOfflineCopy: true });
    });
    void dropPlainCaches(user.id, [noteId], { noteId, epoch });
  }
  const provider: HocuspocusProvider = new HocuspocusProvider({
    websocketProvider: getSocket(),
    name: `note:${noteId}`,
    document: doc,
    token: "session-cookie",
    onSynced: () => setEntry(entry, { state: provider.hasUnsyncedChanges ? "saving" : "synced", everSynced: true, fromOfflineCopy: false }),
    onAuthenticated: ({ scope }) => setEntry(entry, { frozen: scope === "readonly" }),
    onStatus: ({ status }) => {
      if (status === "disconnected" && !["forbidden", "revoked"].includes(entry.state)) setEntry(entry, { state: "offline" });
      if (status === "connecting" && entry.state === "offline") setEntry(entry, { state: "connecting" });
    },
    onUnsyncedChanges: ({ number }) => {
      if (["forbidden", "revoked"].includes(entry.state)) return;
      if (number > 0 && entry.state !== "offline") setEntry(entry, { state: "saving" });
      else if (number === 0 && entry.everSynced && entry.state !== "offline") setEntry(entry, { state: entry.state === "save-error" ? "save-error" : "synced" });
    },
    onStateless: ({ payload }) => {
      try {
        const msg = JSON.parse(payload);
        if (msg.type === "persisted") setEntry(entry, { state: provider.hasUnsyncedChanges ? "saving" : "saved", lastSavedAt: msg.at, error: null });
        if (msg.type === "persist-error") setEntry(entry, { state: "save-error", error: msg.message });
        if (msg.type === "frozen") setEntry(entry, { frozen: !!msg.value });
        if (msg.type === "access-revoked") {
          forget();
          setEntry(entry, { state: "revoked", error: "Your access to this note was removed." });
        }
        if (msg.type === "encryption-changed") {
          forget();
          setEntry(entry, { state: "revoked", error: "This note was end-to-end encrypted. Reopen it to unlock." });
        }
      } catch {
        /* ignore malformed */
      }
    },
    onAuthenticationFailed: ({ reason }) => {
      forget();
      setEntry(entry, {
        state: entry.everSynced ? "revoked" : "forbidden",
        error: reason === "forbidden" ? "You do not have access to this note." : "You cannot open this note right now. Reload the page.",
      });
    },
    onClose: ({ event }) => {
      if (event?.code === 4403) {
        forget();
        setEntry(entry, { state: "revoked", error: event.reason === "encrypted" ? "This note was end-to-end encrypted. Reopen it to unlock." : "Your access to this note was removed." });
      }
    },
  });
  entry.awareness = provider.awareness;
  provider.attach();
  const color = COLORS[Math.abs(hash(user.id)) % COLORS.length];
  provider.setAwarenessField("user", { name: user.displayName.replace(/\s*\(demo\)/, ""), color, colorLight: `${color}33`, id: user.id });
  return entry;
}

export function getCollab(noteId: string, user: { id: string; displayName: string }, epoch = 0): CollabEntry {
  const existing = entries.get(noteId);
  if (existing && existing.kind === "plain" && existing.epoch === epoch) return existing;
  if (existing) releaseCollab(noteId);
  const entry = createPlainEntry(noteId, user, epoch);
  entries.set(noteId, entry);
  return entry;
}

/** Register an entry created elsewhere (encrypted notes). */
export function registerEntry(entry: CollabEntry) {
  const existing = entries.get(entry.noteId);
  if (existing && existing !== entry) releaseCollab(entry.noteId);
  entries.set(entry.noteId, entry);
}

export function peekEntry(noteId: string) {
  return entries.get(noteId) ?? null;
}

export function releaseCollab(noteId: string) {
  const e = entries.get(noteId);
  if (!e) return;
  entries.delete(noteId);
  e.destroy();
}

/** Notes shown somewhere other than their own tab (e.g. the sections of an open manuscript). */
const holds = new Map<string, number>();

/** Keep a note's live connection while it is shown outside a tab; returns the release function. */
export function holdCollab(noteId: string) {
  holds.set(noteId, (holds.get(noteId) ?? 0) + 1);
  return () => {
    const n = (holds.get(noteId) ?? 1) - 1;
    if (n > 0) holds.set(noteId, n);
    else holds.delete(noteId);
  };
}

/** Close connections for notes whose tabs are gone (and that are not shown elsewhere). */
export function retainOnly(noteIds: Set<string>) {
  for (const id of [...entries.keys()]) if (!noteIds.has(id) && !holds.has(id)) releaseCollab(id);
}

/** A notebook or project was deleted: close its notes and remove every offline copy of them here. */
export async function forgetDeletedNotes(userId: string, noteIds: string[]) {
  if (!noteIds.length) return;
  for (const id of noteIds) releaseCollab(id);
  await dropPlainCaches(userId, noteIds);
  const ids = noteIds.map((i) => i.toLowerCase());
  for (const k of await idbKeys(userId).catch(() => [] as string[])) if (ids.some((id) => k.toLowerCase().includes(id))) await idbDel(userId, k);
}

export function releaseAll() {
  for (const id of [...entries.keys()]) releaseCollab(id);
  socket?.destroy();
  socket = null;
}

export function anyUnsynced() {
  for (const e of entries.values()) if (e.hasUnsynced()) return true;
  return false;
}

export function allEntries() {
  return [...entries.values()];
}

function hash(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => {
    if (anyUnsynced()) {
      e.preventDefault();
      e.returnValue = "";
    }
  });
}
